import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { flagEnabled } from "../../config";
import { PasswordField } from "../../components/PasswordField";
import {
  addSmsAccount,
  authkeyError,
  changeSmsAccount,
  flowIdError,
  getSmsGateway,
  listSmsTemplates,
  mapSmsTemplate,
  msg91Text,
  peIdError,
  readTemplateSheet,
  senderIdError,
  sendSmsTest,
  setSmsChoice,
  templateProblem,
  templateSheet,
  type SmsGatewayAccount,
  type SmsMessageType,
  type SmsTemplateRow,
} from "../../api/smsGateway";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * SMS Gateway (owner 2026-10-30): "Operator can use our msg91 gateway, if does
 * not have any objection for our name of AnkhOS or he can configure his own
 * gateway". Applies to SMS to the operator's subscribers; AnkhOS's own messages
 * to the operator and its staff always use AnkhOS's gateway. MSG91 only for now.
 */
export function SmsGatewayScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const enabled = Boolean(identity.token && ns);
  const q = useQuery({ queryKey: ["smsgateway", ns], enabled, queryFn: ({ signal }) => getSmsGateway(identity, ns, signal) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["smsgateway", ns] });
  const [adding, setAdding] = useState(false);
  const [keyFor, setKeyFor] = useState<SmsGatewayAccount | null>(null);
  const [templatesFor, setTemplatesFor] = useState<SmsGatewayAccount | null>(null);

  const own = flagEnabled("sms.gateway.own");
  const toggle = useMutation({ mutationFn: (a: SmsGatewayAccount) => changeSmsAccount(identity, ns, a.id, { enabled: !a.enabled }), onSettled: refresh });
  const choose = useMutation({ mutationFn: (v: { id: string; fallback: boolean }) => setSmsChoice(identity, ns, v.id, v.fallback), onSettled: refresh });

  const accounts = q.data?.accounts ?? [];
  const usable = [...accounts, ...(q.data?.inherited ?? [])];
  const effective = q.data?.effective ?? null;
  const chosen = effective?.gateway_account_id ?? "";
  const fallback = effective?.shared_fallback ?? false;
  const failure = toggle.error ?? choose.error;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="h1">SMS Gateway</Typography>
          <Typography variant="body2" color="text.secondary">
            How your subscribers' SMS are sent. Messages from AnkhOS to you and your staff always use the AnkhOS gateway at no charge.
          </Typography>
        </Box>
        {own && <Button variant="contained" onClick={() => setAdding(true)}>Add new gateway</Button>}
      </Box>

      {q.isError && <Alert severity="error">{errText(q.error)}</Alert>}
      {failure && <Alert severity="error">{errText(failure)}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Send through</Typography>
        <RadioGroup value={chosen} onChange={(e) => choose.mutate({ id: e.target.value, fallback })}>
          <FormControlLabel
            value=""
            control={<Radio />}
            label={`AnkhOS gateway: subscribers see sender ${q.data?.shared_sender ?? "ANKHCT"} and the AnkhOS name. Each SMS is deducted from your SMS fund; login and verification codes are free.`}
          />
          {own && usable.map((a) => (
            <FormControlLabel
              key={a.id}
              value={a.id}
              disabled={!a.enabled || a.status !== "Verified" || !a.approved_at}
              control={<Radio />}
              label={`${a.label} (your MSG91, sender ${a.sender_id})${a.status !== "Verified" ? ": test a template first" : !a.approved_at ? ": send a few test messages and ask AnkhOS to approve it" : ""}`}
            />
          ))}
        </RadioGroup>
        {chosen !== "" && (
          <FormControlLabel
            control={<Checkbox checked={fallback} onChange={(e) => choose.mutate({ id: chosen, fallback: e.target.checked })} />}
            label="Use the AnkhOS gateway when mine has no template or fails (deducted from your SMS fund). If off, that SMS is not sent."
          />
        )}
      </Paper>

      <TemplateSheetPanel catalog={q.data?.catalog ?? []} />

      {own && <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Your SMS gateway accounts">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Gateway</TableCell>
              <TableCell>Sender</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.length === 0 && (
              <TableRow><TableCell colSpan={5}>No account of your own yet.</TableCell></TableRow>
            )}
            {accounts.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.label}</TableCell>
                <TableCell>MSG91</TableCell>
                <TableCell>{a.sender_id}</TableCell>
                <TableCell>
                  <Chip size="small" color={a.status === "Verified" ? "success" : a.status === "Failed" ? "error" : "default"} label={a.enabled ? a.status : "Disabled"} />
                  <Typography variant="caption" color={a.approved_at ? "success.main" : "text.secondary"} display="block">{a.approved_at ? "Approved by AnkhOS" : "Waiting for AnkhOS approval"}</Typography>
                  {a.last_error && <Typography variant="caption" color="error" display="block">{a.last_error}</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                    <Button size="small" onClick={() => setTemplatesFor(a)}>Templates</Button>
                    <Button size="small" onClick={() => setKeyFor(a)}>Replace key</Button>
                    <Button size="small" onClick={() => toggle.mutate(a)}>{a.enabled ? "Disable" : "Enable"}</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary">
        Open Templates to enter your template IDs. Each template is checked with MSG91, then needs one test SMS to your own mobile before it is used.
      </Typography>
      </>}

      {adding && <AddAccountDialog ns={ns} onClose={() => setAdding(false)} onSaved={refresh} />}
      {keyFor && <ReplaceKeyDialog ns={ns} account={keyFor} onClose={() => setKeyFor(null)} onSaved={refresh} />}
      {templatesFor && <TemplatesDialog ns={ns} account={templatesFor} onClose={() => setTemplatesFor(null)} />}
    </Box>
  );
}

/** Publishes AnkhOS's templates for an operator to register under its own name. */
function TemplateSheetPanel({ catalog }: { catalog: SmsMessageType[] }) {
  const [signOff, setSignOff] = useState("");
  const download = () => {
    const url = URL.createObjectURL(new Blob([templateSheet(catalog, signOff)], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "ankhos-sms-templates.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>AnkhOS SMS templates</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        These {catalog.length} messages end with -Ankh Cloud Tech. To end them with your own name, register the same text on your own DLT and MSG91. Only your name at the end may differ. Until then, the AnkhOS gateway sends them.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Your name at the end" placeholder="Test ISP" value={signOff} onChange={(e) => setSignOff(e.target.value)} />
        <Button variant="outlined" disabled={catalog.length === 0} onClick={download}>Download templates</Button>
      </Stack>
    </Paper>
  );
}

function AddAccountDialog({ ns, onClose, onSaved }: { ns: string; onClose: () => void; onSaved: () => void }) {
  const identity = useIdentity();
  const [label, setLabel] = useState("");
  const [sender, setSender] = useState("");
  const [pe, setPe] = useState("");
  const [authkey, setAuthkey] = useState("");
  const save = useMutation({
    mutationFn: () => addSmsAccount(identity, ns, { label: label.trim(), sender_id: sender.trim().toUpperCase(), pe_id: pe.trim(), authkey: authkey.trim() }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const senderErr = sender ? senderIdError(sender) : "";
  const peErr = pe ? peIdError(pe) : "";
  const keyErr = authkey ? authkeyError(authkey) : "";
  const ready = label.trim() !== "" && sender !== "" && pe !== "" && authkey !== "" && !senderErr && !peErr && !keyErr;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add your MSG91 account</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            MSG91 is the only gateway for now; more will follow. You need your own DLT registration and MSG91 templates.
          </Typography>
          {save.isError && <Alert severity="error">{errText(save.error)}</Alert>}
          <TextField label="Label" value={label} onChange={(e) => setLabel(e.target.value)} required />
          <TextField label="Sender ID" value={sender} onChange={(e) => setSender(e.target.value)} required error={Boolean(senderErr)} helperText={senderErr || "Your 6-letter DLT header"} />
          <TextField label="DLT entity ID (PE ID)" value={pe} onChange={(e) => setPe(e.target.value)} required error={Boolean(peErr)} helperText={peErr} />
          <PasswordField label="MSG91 authkey" value={authkey} onChange={(e) => setAuthkey(e.target.value)} required error={Boolean(keyErr)} helperText={keyErr || "Stored encrypted and never shown again"} autoComplete="off" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ready || save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function ReplaceKeyDialog({ ns, account, onClose, onSaved }: { ns: string; account: SmsGatewayAccount; onClose: () => void; onSaved: () => void }) {
  const identity = useIdentity();
  const [authkey, setAuthkey] = useState("");
  const save = useMutation({
    mutationFn: () => changeSmsAccount(identity, ns, account.id, { authkey: authkey.trim() }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const err = authkey ? authkeyError(authkey) : "";
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Replace the authkey of {account.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {save.isError && <Alert severity="error">{errText(save.error)}</Alert>}
          <PasswordField label="New MSG91 authkey" value={authkey} onChange={(e) => setAuthkey(e.target.value)} error={Boolean(err)} helperText={err || "The account stops sending until the test SMS passes again."} autoComplete="off" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={authkey === "" || Boolean(err) || save.isPending} onClick={() => save.mutate()}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

function TemplatesDialog({ ns, account, onClose }: { ns: string; account: SmsGatewayAccount; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [results, setResults] = useState<string[]>([]);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const sheet = readTemplateSheet(await file.text());
      if (typeof sheet === "string") return [sheet];
      if (sheet.length === 0) return ["No template IDs filled in"];
      const out: string[] = [];
      // One at a time: each is checked with MSG91, and one bad row must not stop the rest.
      for (const row of sheet) {
        try {
          await mapSmsTemplate(identity, ns, account.id, row.message_type, row.provider_template_id, row.dlt_template_id);
          out.push(`${row.message_type}: saved`);
        } catch (e) {
          out.push(`${row.message_type}: ${errText(e)}`);
        }
      }
      return out;
    },
    onSuccess: setResults,
    onSettled: () => void qc.invalidateQueries({ queryKey: ["smstemplates", ns, account.id] }),
  });
  const q = useQuery({
    queryKey: ["smstemplates", ns, account.id],
    queryFn: ({ signal }) => listSmsTemplates(identity, ns, account.id, signal),
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Templates of {account.label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Upload the filled-in template sheet, or enter each MSG91 template ID. Each one is checked with MSG91: same text as ours, your name at the end, on header {account.sender_id}. Then send its test SMS.
        </Typography>
        {q.isError && <Alert severity="error">{errText(q.error)}</Alert>}
        <Button component="label" variant="outlined" size="small" disabled={upload.isPending} sx={{ mb: 1 }}>
          {upload.isPending ? "Checking..." : "Upload template sheet"}
          <input hidden type="file" accept=".csv,text/csv" onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) upload.mutate(file);
          }} />
        </Button>
        {upload.isError && <Alert severity="error">{errText(upload.error)}</Alert>}
        {results.length > 0 && <Alert severity="info" sx={{ mb: 1 }}>{results.map((l) => <div key={l}>{l}</div>)}</Alert>}
        <Table size="small" aria-label="Messages and your MSG91 templates">
          <TableBody>
            {(q.data ?? []).map((t) => <TemplateRow key={t.message_type} ns={ns} accountId={account.id} row={t} />)}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function TemplateRow({ ns, accountId, row }: { ns: string; accountId: string; row: SmsTemplateRow }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [value, setValue] = useState(row.provider_template_id);
  const save = useMutation({
    mutationFn: () => mapSmsTemplate(identity, ns, accountId, row.message_type, value),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["smstemplates", ns, accountId] }),
  });
  const test = useMutation({
    mutationFn: () => sendSmsTest(identity, ns, accountId, row.message_type),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["smstemplates", ns, accountId] });
      void qc.invalidateQueries({ queryKey: ["smsgateway", ns] });
    },
  });
  const err = flowIdError(value);
  const problem = templateProblem(row);
  const saved = row.provider_template_id !== "" && !row.stale;
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body2">{msg91Text(row.text)}</Typography>
        <Typography variant="caption" color={row.stale ? "error" : "text.secondary"}>
          {row.message_type}, {row.var_count} values{problem ? `. ${problem}` : ""}
        </Typography>
        {save.isError && <Alert severity="error">{errText(save.error)}</Alert>}
        {test.isError && <Alert severity="error">{errText(test.error)}</Alert>}
      </TableCell>
      <TableCell sx={{ minWidth: 260 }}>
        <Stack direction="row" spacing={1}>
          <TextField size="small" label="MSG91 template ID" value={value} onChange={(e) => setValue(e.target.value)} error={Boolean(err)} helperText={err} />
          <Button size="small" disabled={Boolean(err) || save.isPending || (value.trim() === row.provider_template_id && !row.stale)} onClick={() => save.mutate()}>Save</Button>
          {saved && (row.tested_at
            ? <Chip size="small" color="success" label="Tested" />
            : <Button size="small" disabled={test.isPending} onClick={() => test.mutate()}>Test SMS</Button>)}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
